import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/v1/alert-settings';

const splitNums = (v) => String(v || '').split(',').map(s => s.trim()).filter(Boolean);

// The per-property recipient groups. Each alert type routes to its own list.
const GROUPS = [
  { key: 'management_alert_numbers', label: 'Management Alert Numbers', help: 'Negative review & negative guest-feedback alerts (ER-MOM-01).' },
  { key: 'housekeeping_supervisor_numbers', label: 'Head of Housekeeping', help: 'Room-turn SLA breach alerts (Housekeeping → SLA Settings).' },
  { key: 'maintenance_dept_numbers', label: 'Maintenance Department', help: 'Maintenance work-order SLA / escalation alerts.' },
  { key: 'kitchen_alert_numbers', label: 'Kitchen Manager', help: 'Food-prep SLA breach alerts (KOT prep exceeds the station SLA). ER-MOM-05.' },
];

function PmsAlertSettings({ token, selectedProperty }) {
  const [groups, setGroups] = useState({
    management_alert_numbers: [],
    housekeeping_supervisor_numbers: [],
    maintenance_dept_numbers: [],
    kitchen_alert_numbers: [],
  });
  const [inputs, setInputs] = useState({});
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [feedbackThreshold, setFeedbackThreshold] = useState(2);
  const [canConfigure, setCanConfigure] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const applyData = (data) => {
    setGroups({
      management_alert_numbers: splitNums(data.management_alert_numbers),
      housekeeping_supervisor_numbers: splitNums(data.housekeeping_supervisor_numbers),
      maintenance_dept_numbers: splitNums(data.maintenance_dept_numbers),
      kitchen_alert_numbers: splitNums(data.kitchen_alert_numbers),
    });
    setFeedbackEnabled(data.feedback_alerts_enabled !== false);
    setFeedbackThreshold(Number(data.feedback_alert_threshold) || 2);
  };

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(API, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      applyData(data);
      setCanConfigure(Boolean(data.can_configure));
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const addNumber = (groupKey) => {
    // Accept one or many numbers at once, separated by comma / semicolon / space.
    const parsed = String(inputs[groupKey] || '')
      .split(/[,;\s]+/)
      .map(s => s.trim().replace(/[^\d+]/g, ''))
      .filter(Boolean);
    if (!parsed.length) return;
    const existing = groups[groupKey];
    const toAdd = [];
    const dups = [];
    for (const n of parsed) {
      if (existing.includes(n) || toAdd.includes(n)) dups.push(n);
      else toAdd.push(n);
    }
    if (!toAdd.length) { setError(`Already added: ${dups.join(', ')}`); return; }
    setError(dups.length ? `Skipped duplicate(s): ${dups.join(', ')}` : '');
    setGroups(g => ({ ...g, [groupKey]: [...g[groupKey], ...toAdd] }));
    setInputs(i => ({ ...i, [groupKey]: '' }));
  };
  const removeNumber = (groupKey, n) =>
    setGroups(g => ({ ...g, [groupKey]: g[groupKey].filter(x => x !== n) }));

  const save = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      const res = await fetch(API, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          management_alert_numbers: groups.management_alert_numbers.join(','),
          housekeeping_supervisor_numbers: groups.housekeeping_supervisor_numbers.join(','),
          maintenance_dept_numbers: groups.maintenance_dept_numbers.join(','),
          kitchen_alert_numbers: groups.kitchen_alert_numbers.join(','),
          feedback_alerts_enabled: feedbackEnabled,
          feedback_alert_threshold: Number(feedbackThreshold),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      applyData(data);
      setMessage('Alert settings saved.');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (!selectedProperty?.id) {
    return <div className="page-shell" style={{ color: '#6b7280' }}>Select a property to configure its alert settings.</div>;
  }

  const inp = { padding: '0.6rem', borderRadius: 6, border: '1px solid #dcdde1', fontSize: '0.9rem' };

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        <span>Hotel Config</span> <span style={{ color: '#cbd5e0' }}>›</span> <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Alert Settings</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.25rem' }}>Alert Settings — {selectedProperty?.name || ''}</h2>
      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
        Phone numbers that receive alert SMS for this property, grouped by who should be notified. Each alert type routes to its own group. WhatsApp will use the same lists when enabled.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.7rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>{message}</div>}

      <div style={{ maxWidth: 640, background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem' }}>
        {GROUPS.map((grp, gi) => (
          <div key={grp.key} style={{ marginBottom: '1.4rem', paddingBottom: gi < GROUPS.length - 1 ? '1.2rem' : 0, borderBottom: gi < GROUPS.length - 1 ? '1px solid var(--theme-border)' : 'none' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block' }}>{grp.label}</label>
            <p style={{ fontSize: '0.76rem', color: '#6b7280', margin: '0.15rem 0 0.5rem' }}>{grp.help}</p>

            {groups[grp.key].length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                {groups[grp.key].map(n => (
                  <span key={n} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 20, padding: '0.2rem 0.75rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {n}
                    {canConfigure && <button onClick={() => removeNumber(grp.key, n)} style={{ background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700, lineHeight: 1, padding: '0 2px', fontSize: '0.95rem' }} title="Remove">×</button>}
                  </span>
                ))}
              </div>
            )}

            {canConfigure ? (
              <div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input value={inputs[grp.key] || ''} onChange={e => setInputs(i => ({ ...i, [grp.key]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && addNumber(grp.key)}
                    placeholder="e.g. 9876543210, 9123456780" style={{ ...inp, flex: 1 }} />
                  <button onClick={() => addNumber(grp.key)} style={{ padding: '0.55rem 1rem', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>+ Add</button>
                </div>
                <p style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0.35rem 0 0' }}>Enter one or more numbers separated by commas, then press Enter or Add.</p>
              </div>
            ) : (
              groups[grp.key].length === 0 && <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>None configured.</p>
            )}
          </div>
        ))}

        {/* Negative Guest Feedback alert (routes to Management group) */}
        <div style={{ borderTop: '1px solid var(--theme-border)', margin: '0 0 1rem', paddingTop: '1.2rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', marginBottom: '0.5rem' }}>
            <input type="checkbox" checked={feedbackEnabled} disabled={!canConfigure} onChange={e => setFeedbackEnabled(e.target.checked)} />
            Alert Management on negative guest feedback
          </label>
          <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 0.6rem' }}>
            When a guest rates a service (housekeeping / service request / food order) at or below the threshold in the Guest Portal, the Management numbers get an SMS.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--theme-text)' }}>Threshold:</span>
            <select value={feedbackThreshold} disabled={!canConfigure || !feedbackEnabled} onChange={e => setFeedbackThreshold(Number(e.target.value))} style={{ ...inp, width: 180 }}>
              <option value={1}>≤ 1★ (only 1-star)</option>
              <option value={2}>≤ 2★ (1–2 star)</option>
              <option value={3}>≤ 3★ (1–3 star)</option>
            </select>
          </div>
        </div>

        {loading && <div style={{ color: '#2563eb', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Loading…</div>}

        {canConfigure ? (
          <button onClick={save} disabled={saving} className="btn-primary" style={{ padding: '0.6rem 1.6rem' }}>
            {saving ? 'Saving…' : 'Save Alert Settings'}
          </button>
        ) : (
          <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>You have read-only access. An admin or hotel admin can edit these settings.</p>
        )}
      </div>
    </div>
  );
}

export default PmsAlertSettings;
